// @flow
import type { Dispatch } from "redux"

import { getOr } from "common/utils/get"
import { getLocationsApiUrl } from "common/services/options/reducer"
import { getLanguageInfo } from "common/services/staticData/index"
import { fetchLocationPlaceId } from "common/services/locations/locationsApi"
import { starredStoredSelector } from "common/services/starred/starredSelectors"
import Place from "common/records/Place"
import type { GetState } from "common/services/types"

const RADIUS_PATTERN = /^(.+)-\d+km$/

const addJourneyTripPlace = (place, acc) => {
  const newAcc = acc

  const id = place.get("id")

  if (!newAcc[id]) {
    newAcc[id] = {}
  }

  newAcc[id] = {
    ...newAcc[id],
    id,
    name: place.get("name"),
    code: place.get("code"),
  }
}

const fetchStarredPlaces = () => (dispatch: Dispatch<*>, getState: GetState) => {
  const state = getState()

  const starred = starredStoredSelector(null)
  const apiUrl = getLocationsApiUrl(state)
  const locale = getLanguageInfo(state).iso
  const hasGlobalPlaces = Boolean(state.places)

  const action = () => {
    const newState = getState()
    const statePlaces = newState.places && newState.places.get("places")

    const toMergeGlobal = []

    const { items } = starred

    const placesMap = items.filter(getOr(false, ["form", "places"])).reduce((acc, item) => {
      const newAcc = { ...acc }
      item.journey.trips.forEach(trip => {
        addJourneyTripPlace(trip.getDeparture().get("where"), newAcc)
        addJourneyTripPlace(trip.getArrival().get("where"), newAcc)
      })

      item.form.places.forEach(place => {
        let { id } = place

        if (RADIUS_PATTERN.test(id)) {
          const idMatch = id.match(RADIUS_PATTERN)

          if (idMatch) {
            id = idMatch[1]
          }
        }

        if (!newAcc[id]) {
          newAcc[id] = { id }
        }

        if (!newAcc[id].slug || locale === item.form.lang) {
          newAcc[id].slug = place.slug
        }

        if (statePlaces && !statePlaces.has(id)) {
          toMergeGlobal.push(id)
        }
      })

      return newAcc
    }, {})

    const promiseMap = Object.keys(placesMap).reduce(
      (acc, placeId) => ({
        ...acc,
        [placeId]:
          statePlaces && statePlaces.has(placeId)
            ? statePlaces.get(placeId)
            : fetchLocationPlaceId(apiUrl, locale, placeId)
                .then(place => place || placeId)
                .catch(() => placeId),
      }),
      {},
    )

    dispatch({
      type: "starred/SET_PLACES",
      promise: Promise.all(Object.values(promiseMap)).then(res =>
        res.map(item => (item instanceof Place ? item : new Place(placesMap[String(item)]))),
      ),
    })

    if (toMergeGlobal.length) {
      dispatch({
        type: "FETCH_PLACES_BY_ID",
        promise: Promise.all(toMergeGlobal.map(id => promiseMap[id])),
        toMerge: toMergeGlobal,
      })
    }
  }

  if (hasGlobalPlaces) {
    setTimeout(action, 2000)
  } else {
    action()
  }
}

export default fetchStarredPlaces
